import {useEffect, useState} from 'react';
import dayjs from 'dayjs';
import {Body1, Body2} from '@ui/atoms/typography';
import {TextField} from '@ui/molecules/field/textfield';
import {Calendar} from '@ui/molecules/calendar';
import {RadioSm} from '@ui/atoms/radio';
import {
  CompactCalendarIcon,
  CompactDatePicker,
  DatePickerDropdown,
  EndDateSection,
  OccurrencesRow,
  RadioGroup,
  RadioRowWithField,
  SectionContent,
  SectionSubHeader,
  StartDateSection,
  StartEndStack,
} from './cadence-step.styles';
import {CadenceStepProps} from './types';

const CalendarGlyph = () => (
  <CompactCalendarIcon aria-hidden="true">
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15.8333 3.33594H4.16667C3.24619 3.33594 2.5 4.08213 2.5 5.0026V16.6693C2.5 17.5897 3.24619 18.3359 4.16667 18.3359H15.8333C16.7538 18.3359 17.5 17.5897 17.5 16.6693V5.0026C17.5 4.08213 16.7538 3.33594 15.8333 3.33594Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.3359 1.66406V4.9974M6.66406 1.66406V4.9974M2.5 8.33594H17.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </CompactCalendarIcon>
);

export const CadenceStartEndCard = ({
  compact = false,
  value,
  onChange,
}: CadenceStepProps) => {
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const {
    frequency,
    startOption,
    startDate,
    oneTimeDate,
    endOption,
    occurrences,
    endDate,
  } = value;
  const isOneTime = frequency === 'one-time';
  const selectedStartDate = isOneTime ? oneTimeDate : startDate;

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowStartDatePicker(false);
        setShowEndDatePicker(false);
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, []);

  const updateField = <K extends keyof typeof value>(
    field: K,
    fieldValue: typeof value[K],
  ) => {
    onChange({...value, [field]: fieldValue});
  };

  const selectStartDate = (date: Date) => {
    updateField(
      isOneTime ? 'oneTimeDate' : 'startDate',
      new Date(date).toISOString(),
    );
    setShowStartDatePicker(false);
  };

  return (
    <StartEndStack $compact={compact}>
      <StartDateSection>
        <SectionSubHeader $compact={compact}>
          <Body1 weight="bold" kind="blackTwo">
            Start
          </Body1>
        </SectionSubHeader>
        <SectionContent $compact={compact}>
          <RadioGroup>
            <label className="radio-option">
              <RadioSm
                aria-label="Immediately"
                name="cadence-start"
                checked={startOption === 'immediately'}
                onChange={() => updateField('startOption', 'immediately')}
              />
              <Body2 weight="semibold" kind="blackTwo">
                Immediately
              </Body2>
            </label>

            <RadioRowWithField>
              <label className="radio-option">
                <RadioSm
                  aria-label="On a specific date"
                  name="cadence-start"
                  checked={startOption === 'specific'}
                  onChange={() => updateField('startOption', 'specific')}
                />
                <Body2 weight="semibold" kind="blackTwo">
                  On a specific date
                </Body2>
              </label>

              {startOption === 'specific' && (
                <div style={{position: 'relative'}}>
                  <CompactDatePicker
                    type="button"
                    aria-label="Select start date"
                    aria-expanded={showStartDatePicker}
                    $open={showStartDatePicker}
                    onClick={() => {
                      setShowEndDatePicker(false);
                      setShowStartDatePicker((open) => !open);
                    }}>
                    <Body2 kind={selectedStartDate ? 'textDark' : 'textBody'}>
                      {selectedStartDate
                        ? dayjs(selectedStartDate).format('DD/MM/YYYY')
                        : 'Select date'}
                    </Body2>
                    <CalendarGlyph />
                  </CompactDatePicker>
                  {showStartDatePicker && (
                    <DatePickerDropdown>
                      <Calendar
                        value={selectedStartDate}
                        disabledBefore={new Date().toISOString()}
                        onChange={selectStartDate}
                      />
                    </DatePickerDropdown>
                  )}
                </div>
              )}
            </RadioRowWithField>
          </RadioGroup>
        </SectionContent>
      </StartDateSection>

      {!isOneTime && (
        <EndDateSection>
          <SectionSubHeader $compact={compact}>
            <Body1 weight="bold" kind="blackTwo">
              End
            </Body1>
          </SectionSubHeader>
          <SectionContent $compact={compact}>
            <RadioGroup>
              <label className="radio-option">
                <RadioSm
                  aria-label="Never"
                  name="cadence-end"
                  checked={endOption === 'never'}
                  onChange={() => updateField('endOption', 'never')}
                />
                <Body2 weight="semibold" kind="blackTwo">
                  Never
                </Body2>
              </label>

              <RadioRowWithField>
                <label className="radio-option">
                  <RadioSm
                    aria-label="After"
                    name="cadence-end"
                    checked={endOption === 'after'}
                    onChange={() => updateField('endOption', 'after')}
                  />
                  <Body2 weight="semibold" kind="blackTwo">
                    After
                  </Body2>
                </label>
                {endOption === 'after' && (
                  <OccurrencesRow>
                    <TextField
                      aria-label="Number of occurrences"
                      type="number"
                      min="1"
                      value={occurrences}
                      onChange={(event) =>
                        updateField('occurrences', event.target.value)
                      }
                      margin
                      placeholder="12"
                      inputStyle={{
                        width: '77px',
                        height: '40px',
                        borderRadius: '10px',
                        borderWidth: '1px',
                        padding: '8px 18px',
                      }}
                    />
                    <Body2 weight="semibold" kind="blackTwo">
                      occurrences
                    </Body2>
                  </OccurrencesRow>
                )}
              </RadioRowWithField>

              <RadioRowWithField>
                <label className="radio-option">
                  <RadioSm
                    aria-label="On"
                    name="cadence-end"
                    checked={endOption === 'specific'}
                    onChange={() => updateField('endOption', 'specific')}
                  />
                  <Body2 weight="semibold" kind="blackTwo">
                    On
                  </Body2>
                </label>
                {endOption === 'specific' && (
                  <div style={{position: 'relative'}}>
                    <CompactDatePicker
                      type="button"
                      aria-label="Select end date"
                      aria-expanded={showEndDatePicker}
                      $open={showEndDatePicker}
                      onClick={() => {
                        setShowStartDatePicker(false);
                        setShowEndDatePicker((open) => !open);
                      }}>
                      <Body2 kind={endDate ? 'textDark' : 'textBody'}>
                        {endDate
                          ? dayjs(endDate).format('DD/MM/YYYY')
                          : 'Select date'}
                      </Body2>
                      <CalendarGlyph />
                    </CompactDatePicker>
                    {showEndDatePicker && (
                      <DatePickerDropdown>
                        <Calendar
                          value={endDate}
                          disabledBefore={
                            selectedStartDate || new Date().toISOString()
                          }
                          onChange={(date) => {
                            updateField(
                              'endDate',
                              new Date(date).toISOString(),
                            );
                            setShowEndDatePicker(false);
                          }}
                        />
                      </DatePickerDropdown>
                    )}
                  </div>
                )}
              </RadioRowWithField>
            </RadioGroup>
          </SectionContent>
        </EndDateSection>
      )}
    </StartEndStack>
  );
};
